import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import LeiderschapLayout from '@/components/LeiderschapLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { leiderschapApi } from '../api';
import SectionHeader from '../components/SectionHeader';

function parseNumberList(value: string): number[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => Number(part))
    .filter((n) => !Number.isNaN(n));
}

export default function SettingsPage() {
  const [hoursBeforeMeeting, setHoursBeforeMeeting] = useState('24');
  const [daysBeforeActionItemDue, setDaysBeforeActionItemDue] = useState('3, 1');
  const [notifyOnOverdue, setNotifyOnOverdue] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const settings = await leiderschapApi.getNotificationSettings();
        setHoursBeforeMeeting(settings.hours_before_meeting.join(', '));
        setDaysBeforeActionItemDue(settings.days_before_action_item_due.join(', '));
        setNotifyOnOverdue(settings.notify_on_action_item_overdue);
      } catch (error) {
        toast.error('Kon meldingsinstellingen niet laden');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await leiderschapApi.updateNotificationSettings({
        hours_before_meeting: parseNumberList(hoursBeforeMeeting),
        days_before_action_item_due: parseNumberList(daysBeforeActionItemDue),
        notify_on_action_item_overdue: notifyOnOverdue,
      });
      toast.success('Meldingsinstellingen opgeslagen');
    } catch (error) {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LeiderschapLayout><SectionHeader title="Instellingen" /></LeiderschapLayout>;

  return (
    <LeiderschapLayout>
      <SectionHeader title="Instellingen" description="Meldingsinstellingen voor vergaderingen en actiepunten." />
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">Meldingsinstellingen</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="hours-before-meeting">Vergaderherinnering (uren vooraf)</Label>
              <Input
                id="hours-before-meeting"
                value={hoursBeforeMeeting}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHoursBeforeMeeting(e.target.value)}
                placeholder="bijv. 24, 2"
              />
              <p className="mt-1 text-xs text-slate-500">Kommagescheiden lijst, bijv. "24, 2" voor een dag en twee uur vooraf.</p>
            </div>
            <div>
              <Label htmlFor="days-before-due">Actiepunt-herinnering (dagen voor deadline)</Label>
              <Input
                id="days-before-due"
                value={daysBeforeActionItemDue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDaysBeforeActionItemDue(e.target.value)}
                placeholder="bijv. 3, 1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="notify-overdue"
                checked={notifyOnOverdue}
                onCheckedChange={(checked: boolean) => setNotifyOnOverdue(checked === true)}
              />
              <Label htmlFor="notify-overdue" className="font-normal">
                Melden wanneer een actiepunt de deadline overschrijdt
              </Label>
            </div>
            <Button type="submit" disabled={saving}>Opslaan</Button>
          </form>
        </CardContent>
      </Card>
    </LeiderschapLayout>
  );
}
